import type { AuthErrorScenario } from '~/types/authError';

/**
 * Result of extracting Clerk error code with scenario classification.
 */
export interface ClerkErrorCodeResult {
  code: string | null;
  scenario: AuthErrorScenario;
  shouldRedirect: boolean;
}

/**
 * Clerk error structure from API responses.
 */
interface ClerkError {
  message?: string;
  longMessage?: string;
  code?: string;
}

interface ClerkErrorResponse {
  errors?: ClerkError[];
}

/**
 * Error code to scenario mapping table.
 *
 * Security Note: user_banned and identifier_already_exists both map to
 * 'account-suspended' to prevent email enumeration attacks.
 *
 * Backend API codes: registration_closed, account_suspended
 * Clerk codes: user_banned, identifier_already_exists, sign_up_restricted, etc.
 */
const ERROR_CODE_MAPPING: Record<string, { scenario: AuthErrorScenario; shouldRedirect: boolean }> =
  {
    // Clerk error codes
    user_banned: { scenario: 'account-suspended', shouldRedirect: true },
    identifier_already_exists: {
      scenario: 'account-suspended',
      shouldRedirect: true,
    },
    sign_up_restricted: { scenario: 'registration-closed', shouldRedirect: true },
    // Set on the OAuth verification when the instance's sign-up mode is
    // Restricted or Waitlist and a new account comes back from the provider
    sign_up_mode_restricted: { scenario: 'registration-closed', shouldRedirect: true },
    sign_up_restricted_waitlist: { scenario: 'registration-closed', shouldRedirect: true },
    form_identifier_not_found: { scenario: 'registration-closed', shouldRedirect: true },
    user_quota_exceeded: {
      scenario: 'registration-closed',
      shouldRedirect: true,
    },
    // Backend API error codes
    registration_closed: { scenario: 'registration-closed', shouldRedirect: true },
    account_suspended: { scenario: 'account-suspended', shouldRedirect: true },
    trial_expired: { scenario: 'trial-expired', shouldRedirect: true },
  };

/**
 * Backend API error structure for restriction errors.
 */
interface BackendApiError {
  response?: {
    status?: number;
    data?: {
      detail?:
        | string
        | {
            message?: string;
            code?: string;
          };
    };
  };
}

/**
 * Extract error code from backend API restriction errors.
 *
 * Backend returns 403 with structured error detail:
 * { detail: { message: "...", code: "registration_closed" | "account_suspended" } }
 *
 * @param error - Unknown error object from backend API
 * @returns Classification result with code, scenario, and redirect flag
 */
function extractApiErrorCode(error: unknown): ClerkErrorCodeResult {
  if (!error || typeof error !== 'object') {
    return {
      code: null,
      scenario: 'unknown',
      shouldRedirect: false,
    };
  }

  const apiError = error as BackendApiError;

  // Check for 403 status with restriction codes (real AxiosError shape)
  if (apiError.response?.status === 403) {
    const detail = apiError.response.data?.detail;

    // Handle structured error detail
    if (typeof detail === 'object' && detail?.code) {
      const mapping = ERROR_CODE_MAPPING[detail.code];

      if (mapping) {
        return {
          code: detail.code,
          scenario: mapping.scenario,
          shouldRedirect: mapping.shouldRedirect,
        };
      }
    }
  }

  return {
    code: null,
    scenario: 'unknown',
    shouldRedirect: false,
  };
}

/**
 * Extract Clerk error code and classify into actionable scenario.
 *
 * Per §TS.2.3, follow defensive pattern for handling unknown types.
 * Maps known Clerk error codes to authentication scenarios for error handling.
 *
 * @param error - Unknown error object from Clerk API
 * @returns Classification result with code, scenario, and redirect flag
 *
 * @example
 * const result = extractClerkErrorCode(error);
 * if (result.shouldRedirect) {
 *   navigate(`/error/${result.scenario}`);
 * }
 */
export function extractClerkErrorCode(error: unknown): ClerkErrorCodeResult {
  if (!error || typeof error !== 'object') {
    return {
      code: null,
      scenario: 'unknown',
      shouldRedirect: false,
    };
  }

  const clerkError = error as ClerkErrorResponse;
  const firstError = clerkError.errors?.[0];
  const code = firstError?.code;

  if (!code) {
    return {
      code: null,
      scenario: 'unknown',
      shouldRedirect: false,
    };
  }

  const mapping = ERROR_CODE_MAPPING[code];

  if (!mapping) {
    return {
      code,
      scenario: 'unknown',
      shouldRedirect: false,
    };
  }

  return {
    code,
    scenario: mapping.scenario,
    shouldRedirect: mapping.shouldRedirect,
  };
}

/**
 * Extract error code from either Clerk or backend API errors.
 *
 * Checks for backend API restriction errors (403) first, then falls back to Clerk errors.
 * Per §BASIC.2, extract common logic to avoid duplication.
 *
 * @param error - Unknown error object from Clerk API or backend API
 * @returns Classification result with code, scenario, and redirect flag
 *
 * @example
 * const result = extractAuthErrorCode(error);
 * if (result.shouldRedirect) {
 *   navigate(`/auth-error?type=${result.scenario}`, { replace: true });
 * }
 */
export function extractAuthErrorCode(error: unknown): ClerkErrorCodeResult {
  // Try API error extraction first (more specific)
  const apiResult = extractApiErrorCode(error);
  if (apiResult.shouldRedirect) {
    return apiResult;
  }

  // Fall back to Clerk error extraction
  return extractClerkErrorCode(error);
}

/** Copy shown when the account does not exist and sign-up is closed. */
export const REGISTRATION_CLOSED_MESSAGE =
  'This account does not exist, and registrations are closed.';

/**
 * Reason codes that mean the account does not exist and registration is closed.
 *
 * These are treated identically for messaging purposes even though they come
 * from different Clerk failure paths (sign-up restriction vs. sign-in lookup).
 */
const INVITE_ONLY_REASONS = new Set([
  'not_allowed_access',
  'sign_up_mode_restricted',
  'sign_up_restricted_waitlist',
  'sign_up_restricted',
  'form_identifier_not_found',
  'user_quota_exceeded',
  'registration_closed',
]);

/**
 * Map a raw Clerk error code (as it would appear in the `?reason=` query
 * param on the sign-in page) to user-facing banner copy. The code comes from
 * the URL, so it is never echoed into the returned message.
 *
 * @param reason - Raw `reason` search param value, if present
 * @returns Banner message, or null when there is nothing to show
 */
export function getSignInReasonMessage(reason?: string): string | null {
  if (!reason) {
    return null;
  }

  if (INVITE_ONLY_REASONS.has(reason)) {
    return REGISTRATION_CLOSED_MESSAGE;
  }

  if (reason === 'external_account_not_found') {
    return 'No DeadDrop account uses this Google/GitHub login. Try the email you signed up with.';
  }

  if (reason === 'user_locked') {
    return 'Too many attempts. Try again in a few minutes.';
  }

  return "We couldn't sign you in. Try again or contact support.";
}
